import {
  createContext,
  useContext,
  type HTMLAttributes,
  type ReactNode,
} from 'react'

/**
 * The shared Local page shell (M20-T01, ADR-0044).
 *
 * Sixteen Local screens used to hand-write `mx-auto flex min-h-screen max-w-2xl
 * flex-col …` — in two widths nobody chose. This is the one place that decides
 * instead: page chrome runs full width, and content sits on a measure the
 * screen states once.
 *
 * A screen wraps itself in `PageShell` and puts its padded content in
 * `PageContent`. Chrome — bars, headers, coloured bands — goes straight into
 * `PageShell` so it stays full-bleed, and picks up `PAGE_GUTTER_DESKTOP` so its
 * contents do not sit flush against the edge of a wide viewport.
 */

/**
 * The content measures the Figma 1440 frames draw. Deliberately a closed union:
 * a screen cannot invent a seventeenth width, because that is the failure this
 * shell exists to stop (ADR-0044). Adding one means amending the ADR and this
 * type together.
 */
export type Measure = 520 | 594 | 640 | 672 | 'full'

const MEASURE_CLASS: Record<Measure, string> = {
  520: 'max-w-[520px]', // the profile info card
  594: 'max-w-[594px]', // specialists, beds
  640: 'max-w-[640px]', // medicines, diagnostics, emergency
  672: 'max-w-[672px]', // prose only — LegalPage, Contact (~70 characters)
  full: 'max-w-none', // the hospital results card grid
}

/**
 * Desktop side padding: 32px from `sm`, 48px from `lg`.
 *
 * Figma specifies a 48px (`--s-12`) gutter and both shipped homes use 32px. The
 * ramp is not a compromise between them — Figma's frame is 1440, which is an
 * `lg` viewport, so `lg:px-12` is Figma's number at Figma's width while
 * `sm:px-8` matches everything already shipped. See ADR-0044.
 *
 * Exported so full-bleed chrome ramps from the same definition as content.
 * Mobile padding is deliberately NOT included: chrome bars carry `px-3` and
 * content carries `px-4` today, and M20 does not change mobile.
 */
export const PAGE_GUTTER_DESKTOP = 'sm:px-8 lg:px-12'

const MeasureContext = createContext<Measure>('full')

/**
 * A full-bleed band whose CONTENT sits on the shell's measure.
 *
 * For a coloured alert, a rule, a progress rail: the background must reach the
 * viewport edge, but the copy inside it belongs on the content column. Without
 * this, full-bleed chrome stretches its contents — on `Emergency` the alert
 * paragraph reached 1113px at 1440 (from 622px) and the four-step rail spread
 * to 436px per step (from 168px), both measured, not guessed.
 *
 * `PageContent` is deliberately NOT the answer for these: it imposes the
 * shell's 16px mobile gutter, and bands carry their own (12px on `Emergency`,
 * 8px on the profile tab strip), so reusing it would move mobile. `PageBand`
 * adds a measure and nothing else — the band keeps its own padding via
 * `className`.
 *
 * Any other prop lands on the MEASURED element, not the band. That is the
 * semantic one: `role="tablist"` has to be the direct parent of its tabs or
 * `aria-required-children` fails, and the band is only the rule behind it.
 */
export function PageBand({
  className,
  contentClassName,
  children,
  ...contentProps
}: {
  className?: string
  contentClassName?: string
  children: ReactNode
} & HTMLAttributes<HTMLDivElement>): ReactNode {
  const measure = useContext(MeasureContext)
  return (
    <div className={className}>
      <div
        {...contentProps}
        className={`mx-auto w-full ${MEASURE_CLASS[measure]}${contentClassName ? ` ${contentClassName}` : ''}`}
      >
        {children}
      </div>
    </div>
  )
}

export function PageShell({
  measure,
  children,
  className,
  as: Tag = 'div',
}: {
  measure: Measure
  children: ReactNode
  className?: string
  /**
   * The element to render. Defaults to `div`; the bed flow's three screens are
   * `main` and must stay `main` — dropping the landmark to adopt the shell
   * would trade a width fix for an accessibility regression.
   */
  as?: 'div' | 'main'
}): ReactNode {
  return (
    <MeasureContext.Provider value={measure}>
      <Tag
        className={`flex min-h-screen flex-col bg-bg-app${className ? ` ${className}` : ''}`}
      >
        {children}
      </Tag>
    </MeasureContext.Provider>
  )
}

/**
 * Content on the shell's measure. Reads the measure from context rather than
 * taking it as a prop, so a screen with chrome interleaved between its content
 * blocks — `Emergency` has a header per step — still states its width exactly
 * once.
 *
 * Two elements, not one: with `border-box` sizing, padding on the measured
 * element would eat the measure, so a screen asking for 640px of content would
 * get 544px at `lg`. The gutter goes outside, the measure inside, and the
 * stated number is the number the content gets.
 */
export function PageContent({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}): ReactNode {
  const measure = useContext(MeasureContext)
  return (
    <div className={`flex w-full flex-1 flex-col px-4 ${PAGE_GUTTER_DESKTOP}`}>
      <div
        className={`mx-auto flex w-full flex-1 flex-col ${MEASURE_CLASS[measure]}${className ? ` ${className}` : ''}`}
      >
        {children}
      </div>
    </div>
  )
}
